import { Cart } from "@omni/types/api/cart/bff/cart.model";
import { Product } from "@omni/types/api/product/bff/product.model";
import { ProductFieldSet } from "@omni/types/api/product/bff/shared/enums/productFieldSet.enum";
import { cn } from "@omni/utils/styles";
import { useEffect } from "react";
import { useSelector } from "react-redux";

import { BundleProductModel } from "@/api/mappers/productBundles.mapper";
import { ProductBundlePrice } from "@/components/Product/ProductBundles/components/ProductBundlePrice/productBundlePrice.container";
import { useThunkDispatch } from "@/redux/dispatcher";
import * as CartModule from "@/redux/modules/api/data/cart/cart.module";
import * as CartSelectors from "@/redux/modules/api/data/cart/cart.selectors";
import { useGranite } from "@/redux/modules/value/translations/translations.provider";

import { BundleProductComponent } from "../BundleProduct/bundleProduct.component";
import { ProductBundleAddToCartButtonComponent } from "../ProductBundleAddToCartButton/productBundleAddToCartButton.component";

type ProductBundleComponentProps = {
    mainProduct: Product<ProductFieldSet.ProductTile>;
    mainProductAmount: number;
    bundleProducts: Array<BundleProductModel>;
    cart?: Cart;
    onAddToCart: () => void;
    addToCartLoading: boolean;
};

const ProductBundleComponent = ({
    mainProduct,
    mainProductAmount,
    bundleProducts,
    onAddToCart,
    addToCartLoading
}: ProductBundleComponentProps) => {
    const { t } = useGranite();

    const dispatch = useThunkDispatch();

    const cart = useSelector(CartSelectors.getCart);
    const cartLoading = useSelector(CartSelectors.isLoading);

    const products = [
        {
            product: mainProduct,
            quantity: mainProductAmount
        },
        ...bundleProducts
    ];

    useEffect(() => {
        if (!cart && !cartLoading) {
            dispatch(CartModule.load());
        }
    }, [cart, cartLoading, dispatch]);

    return (
        <section
            className={cn(
                "tw:rounded-md tw:overflow-hidden tw:border tw:border-grey-light",
                "tw:not-last:mb-8"
            )}
        >
            <div
                className={cn(
                    "tw:xl:flex tw:xl:mx-4 tw:xl:overflow-auto tw:xl:flex-nowrap tw:xl:items-stretch"
                )}
            >
                {products.map((bundleProduct, index) => (
                    <>
                        {!!index && (
                            <div
                                className={cn(
                                    "tw:relative tw:border-b tw:border-grey-light",
                                    "tw:xl:border-none"
                                )}
                            >
                                <i
                                    className={cn(
                                        "las la-lg la-plus",
                                        "tw:absolute tw:top-1/2 tw:left-1/2 tw:-translate-x-1/2 tw:-translate-y-1/2 tw:bg-white tw:z-1 tw:text-2xl tw:w-8 tw:h-8 tw:flex tw:justify-center tw:items-center tw:font-bold",
                                        "tw:xl:relative tw:xl:bg-transparent"
                                    )}
                                />
                            </div>
                        )}
                        <BundleProductComponent
                            key={bundleProduct.product.sku}
                            bundleProduct={bundleProduct}
                        />
                    </>
                ))}
            </div>
            <div
                className={cn(
                    "tw:rounded-b-md tw:bg-grey tw:px-4 tw:py-2 tw:flex tw:justify-end tw:items-center"
                )}
            >
                <p className={cn("tw:mb-0 tw:grow tw:text-left")}>
                    {t("general.productBundles.combinationPrice")}
                </p>
                <ProductBundlePrice
                    className={cn("tw:mr-4")}
                    bundleProducts={[
                        {
                            product: mainProduct,
                            quantity: mainProductAmount
                        },
                        ...bundleProducts
                    ]}
                />
                <ProductBundleAddToCartButtonComponent
                    bundleSkus={[
                        mainProduct.sku,
                        ...bundleProducts.map(({ product }) => product.sku)
                    ]}
                    cart={cart}
                    onAddToCart={onAddToCart}
                    addToCartLoading={addToCartLoading}
                />
            </div>
        </section>
    );
};

export { ProductBundleComponent };
